/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { graphql } from 'react-apollo';

import type { XdpV1_Org_Coursera_Xdp_Sdp_SdpCourse as Course } from '__generated__/graphql-types';
import { compose, setDisplayName, withProps } from 'recompose';

import type { Theme } from '@coursera/cds-core';
import { Grid, Link, ProductCard as LoadingProductCard, Typography2, useTheme } from '@coursera/cds-core';
import { ArrowNextIcon } from '@coursera/cds-icons';
import type { ProductType } from '@coursera/event-pulse-types';
import { useTracker } from '@coursera/event-pulse/react';

import { ProductQuery } from 'bundles/front-page/common/Queries';
import type {
  ProductQuery as ProductQueryType,
  ProductQueryVariables,
  ProductQuery_XdpV1Resource_slug_elements_xdpMetadata_XdpV1_sdpMetadataMember as SdpMetadataMember,
} from 'bundles/front-page/common/__generated__/ProductQuery';
import ProductCardReviews from 'bundles/front-page/components/modules/career-roles/ProductCardReviews';
import {
  PRODUCT_DIFFICULTY_LEVELS,
  PRODUCT_VARIANT_TO_ENTITY_TYPE_MAPPING,
} from 'bundles/front-page/constants/learningProduct';
import { TrackedLink2 } from 'bundles/page/components/TrackedLink2';
import ProductCard from 'bundles/product-card/components/legacy/ProductCard';
import { styles as skillStyles } from 'bundles/product-card/components/legacy/ProductCardSkills';
import type { CardProps, EventingV3ProductCardData } from 'bundles/product-card/components/legacy/types/ProductCard';
import { eventingV3ProductType, extractEventId } from 'bundles/product-card/components/utils/productCardV2Utils';
import { getAriaLabel } from 'bundles/product-card/utils/productCardUtils';
import { getTotalMonthsFromCourses } from 'bundles/program-common/utils/productDuration';
import getSkillTranslations from 'bundles/search-common/utils/getSkillTranslations';

import _t from 'i18n!nls/front-page';

type InputProps = {
  slugs: Array<string>;
  roleName: string;
};

type ProductDataProps = {
  productData?: Array<CardProps & { slug?: string }>;
};

type Props = ProductDataProps & InputProps;

const styles = {
  cardContainer: (theme: Theme) => css`
    ${theme.breakpoints.up('sm')} {
      max-width: 360px;
    }
  `,
  card: (theme: Theme) => css`
    height: calc(100% - 16px);

    a {
      display: block;
      height: inherit;
      overflow: hidden;
      border-radius: 8px;
      box-shadow: rgb(0 0 0 / 10%) 0 2px 10px;
      transition: transform 100ms ease-in, box-shadow 100ms linear;
      transform-origin: 50% 100%;

      :hover,
      :focus {
        outline: none;
        ${theme.breakpoints.up('sm')} {
          box-shadow: rgb(0 0 0 / 24%) 0 0 40px -8px, rgb(0 0 0 / 20%) 0 0 25px -16px;
          transform: scale3d(1.03, 1.03, 1.08) translate3d(0.1rem, -0.25rem, 20rem);
        }
      }
    }
  `,
  cardHeader: (theme: Theme) => css`
    margin-top: ${theme.spacing(8)};
  `,
  linkContainer: css`
    display: flex;
    justify-content: end;
  `,
};

export const ProductCards = ({ productData, roleName }: Props) => {
  const theme = useTheme();
  const track = useTracker();
  const translationsMap = getSkillTranslations();

  if (!productData?.length) {
    return (
      <Grid container spacing={32}>
        <Grid css={styles.cardContainer} item sm={6} xs={12}>
          <LoadingProductCard loading={true} productType="" variant="grid" />
        </Grid>
        <Grid css={styles.cardContainer} item sm={6} xs={12}>
          <LoadingProductCard loading={true} productType="" variant="grid" />
        </Grid>
      </Grid>
    );
  }

  const handleClick = (trackingData: EventingV3ProductCardData) => {
    track('click_product_card', trackingData);
  };
  const handleKeyDown = (event: React.KeyboardEvent, trackingData: EventingV3ProductCardData) => {
    if (event.key === 'Enter' || event.key === ' ') {
      handleClick(trackingData);
    }
  };

  return (
    <Grid container spacing={32}>
      {productData.map((product, index) => {
        const { id, name, slug, productType, skills } = product;
        const collectionName = `Career Roles: ${roleName}`;
        const translatedSkills = skills?.map((skill) => translationsMap[skill] || skill);

        const eventingV3TrackingData = {
          productCard: {
            index: index ? index + 1 : 1,
          },
          product: {
            id: extractEventId(id) as string,
            name: name ?? '',
            slug: slug as string,
            type: eventingV3ProductType(productType) as ProductType,
          },
          carousel: {
            name: collectionName,
            id: collectionName ?? '',
            section: 0,
          },
        };

        return (
          <Grid item sm={6} key={product.id} css={styles.cardContainer} xs={12}>
            <div css={styles.card}>
              <TrackedLink2
                href={`/professional-certificates/${product.slug}?trk_ref=camodule`}
                trackingName="ca_module_product_card"
                withVisibilityTracking={false}
                requireFullyVisible={false}
                target="_blank"
                rel="noopener noreferrer"
                data={{ selectedRole: roleName, productSlug: product.slug }}
                style={{
                  textDecoration: 'none',
                }}
                ariaLabel={getAriaLabel(product)}
                onKeyDown={(e) => handleKeyDown(e, eventingV3TrackingData)}
                onClick={() => handleClick(eventingV3TrackingData)}
              >
                <ProductCard
                  cardProps={product}
                  cardHeader={
                    <Typography2 component="h3" variant="subtitleMedium" css={styles.cardHeader}>
                      {product.name}
                    </Typography2>
                  }
                  skills={
                    translatedSkills?.length ? (
                      <Typography2
                        component="p"
                        variant="bodySecondary"
                        color="supportText"
                        css={skillStyles.skillsContainer}
                      >
                        {_t("Skills you'll gain: ")}
                        {translatedSkills.join(', ')}
                      </Typography2>
                    ) : undefined
                  }
                  reviews={
                    <ProductCardReviews
                      avgProductRating={product?.avgProductRating}
                      numProductRatings={product?.numProductRatings}
                    />
                  }
                  slug={product.slug}
                  eventingV3TrackingData={eventingV3TrackingData}
                />
              </TrackedLink2>
            </div>
            {index === productData.length - 1 && (
              <div css={styles.linkContainer}>
                <Link
                  href="/professional-certificates?trk_ref=camodule"
                  target="_blank"
                  icon={<ArrowNextIcon />}
                  iconPosition="after"
                  variant="quiet"
                  rel="noopener noreferrer"
                  typographyVariant="subtitleMedium"
                  component={TrackedLink2}
                  trackingName="ca_module_explore_all_certificates"
                  data={{ selectedRole: roleName }}
                  style={{
                    // use inline style to override CDS's link color
                    color: theme.palette.black[500],
                  }}
                >
                  {_t('Explore all certificates')}
                </Link>
              </div>
            )}
          </Grid>
        );
      })}
    </Grid>
  );
};

// TODO: https://coursera.atlassian.net/browse/CORDISC-897
// move the graphql call to the page-level data provider, FrontPageDataProvider
const withProduct = (getVariablesFromProps: (props: InputProps) => ProductQueryVariables) =>
  graphql<
    InputProps & { productData: Array<Record<string, unknown>> },
    ProductQueryType,
    ProductQueryVariables,
    ProductDataProps
  >(ProductQuery, {
    skip: (props) => !getVariablesFromProps(props).slug,
    options: (props) => {
      return { variables: { ...getVariablesFromProps(props) } };
    },
    props: ({ data, ownProps }) => {
      const apiData = data?.XdpV1Resource?.slug?.elements?.[0]?.xdpMetadata as SdpMetadataMember;

      const {
        id,
        slug,
        name,
        certificateLogo,
        level,
        skills,
        s12nAndCourseSkills,
        ratings,
        productVariant,
        monthsToCompleteOverride,
        partners,
        coBrandingEnabled,
        courses,
      } = apiData?.sdpMetadata || {};

      if (!id) {
        return { productData: [...ownProps.productData] };
      }

      const partnerLogos = partners?.map((partner) => partner.squareLogo);
      const numberOfMonthsToComplete = monthsToCompleteOverride || getTotalMonthsFromCourses(courses as Course[]);

      return {
        ...ownProps,
        productData: [
          ...ownProps.productData,
          {
            id,
            name,
            slug,
            imageUrl: certificateLogo,
            productDifficultyLevel: level && PRODUCT_DIFFICULTY_LEVELS[level as keyof typeof PRODUCT_DIFFICULTY_LEVELS],
            skills: s12nAndCourseSkills?.length ? s12nAndCourseSkills : skills,
            avgProductRating: ratings?.averageFiveStarRating,
            numProductRatings: ratings?.ratingCount,
            productType:
              PRODUCT_VARIANT_TO_ENTITY_TYPE_MAPPING[
                productVariant as keyof typeof PRODUCT_VARIANT_TO_ENTITY_TYPE_MAPPING
              ],
            partners,
            partnerLogos,
            coBrandingEnabled,
            productDuration:
              numberOfMonthsToComplete > 0
                ? _t('#{numberOfMonthsToComplete} months', { numberOfMonthsToComplete })
                : undefined,
          },
        ],
      };
    },
  });

export default compose<Props, InputProps>(
  withProps(() => ({
    productData: [],
  })),
  withProduct(({ slugs }) => ({ slug: slugs[0] })),
  withProduct(({ slugs }) => ({ slug: slugs[1] })),
  setDisplayName('ProdcutCardsHOC')
)(ProductCards);
